/**
 * @file 赎回结果弹窗
 */
import { useEffect, useMemo } from 'react';
import { Modal, Button } from '@bfe/moly';
import { useFm } from '@web3/base-hooks';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { useParams } from '@web3/base-hooks';
import { GasStore, UnstakeCoinInfoStore } from '@/types/unstake';
import { useRefreshContext } from '@/hooks/useRefresh';
import { PendingArr, FailArr, SuccessArr } from '@/config';
import { useCoinInfoContext } from '@/hooks/useCoinInfo';
import { UnstakeContent } from '../unstakeContent';
import {
  OrderResultPending,
  OrderResultSuccess,
  OrderResultFail,
  Row,
  SlippageLostRow,
} from '@/components';
import { MultiIconSpecial } from '@/components/multiIcon';
import cls from 'classnames';
import { useOrderStatus } from '@/hooks/order';
import { useCurrentChainAddress } from '@/hooks/wallet';
import { formatUSDYield } from '@/utils/num';
import { scTrack } from '@web3/base-tracking';
import { Event } from '@/config/track-config';
import { DelayRedeemInfo } from '@/types/unstake';
import { ShowType } from '@/types/account';
import styles from './index.module.scss';
import {
  DelayPeriod,
  DelayRedeemNotify,
} from '@/containers/operation/account/unstakeInfo/components';
import { appPath } from '@web3/base-utils';

interface Props {
  show: boolean;
  unstakeAmount: string;
  slippage: string;
  gasStore: GasStore;
  selectCoinList: string[];
  slippageIsLost: boolean;
  unstakeCoinAmountStore: UnstakeCoinInfoStore;
  orderCode: string;
  delayRedeemInfo: DelayRedeemInfo;
  showType: ShowType;
}

export const OrderResultModal = (props: Props) => {
  const {
    show,
    unstakeAmount,
    slippage,
    gasStore,
    selectCoinList,
    unstakeCoinAmountStore,
    slippageIsLost,
    delayRedeemInfo,
    showType,
    orderCode,
  } = props;
  const { poolInfo } = usePoolInfoContext();
  const fm = useFm();
  const { locale } = useParams();
  const { refresh } = useRefreshContext();
  const { coinInfoStore } = useCoinInfoContext();
  const { walletAddress } = useCurrentChainAddress(poolInfo.chainCode);

  const onConfirm = () => {
    scTrack(Event.Web3UserClick, {
      page_name: 'StakingWeb_bbSOLPageredeemresult',
      button_name: 'check_order_detail',
    });
    if (window.top) {
      window.top.location.href = `/${locale}${appPath}/history?chainId${poolInfo.chainId}&poolId=${poolInfo.id}`;
    }
  };

  const { orderStatusData } = useOrderStatus(orderCode);

  useEffect(() => {
    const params = {
      page_name: 'StakingWeb_bbSOLPageredeemresult',
      orderresultName: 'pending',
    };

    if (SuccessArr.includes(orderStatusData.status)) {
      params.orderresultName = 'succeed';
    }

    if (FailArr.includes(orderStatusData.status)) {
      params.orderresultName = 'fail';
    }
    scTrack(Event.Web3PageView, params);
  }, [orderStatusData]);

  const useHexPrefix = useMemo(() => {
    if (poolInfo?.chainCode === 'SOL') return false;
    return true;
  }, [poolInfo]);

  return (
    <Modal
      open={show}
      onOk={onConfirm}
      onCancel={() => {
        refresh();
        scTrack(Event.Web3UserClick, {
          page_name: 'StakingWeb_bbSOLPageredeemresult',
          button_name: 'cancel',
        });
      }}
      width={480}
      footer={null}
      modal={false}
      title={fm('staking_confirm_redemption')}
    >
      {PendingArr.includes(orderStatusData.status) && (
        <OrderResultPending finishTime={orderStatusData.finishTime}>
          <div className={styles.pendingContent}>
            <UnstakeContent
              selectCoinList={selectCoinList}
              unstakeAmount={unstakeAmount}
              unstakeCoinAmountStore={unstakeCoinAmountStore}
            />
          </div>
          <DelayPeriod
            show={showType === ShowType.DelayUnStake}
            delayRedeemInfo={delayRedeemInfo}
          />
          <SlippageLostRow
            show={!!slippage}
            slippage={slippage}
            isWarn={slippageIsLost}
          />
          <Row
            label={fm('estimated_cost')}
            className={cls('mt-3', styles.cost)}
            value={
              <div>
                <div
                  className={styles.amount}
                >{`${gasStore.amount} ${gasStore.symbol}`}</div>
                <div className={styles.price}>
                  {`${formatUSDYield(gasStore.price)}`}
                </div>
              </div>
            }
          />
        </OrderResultPending>
      )}
      {SuccessArr.includes(orderStatusData.status) && (
        <OrderResultSuccess
          data={[
            {
              label: fm('fromContractAddress'),
              address: poolInfo.contractAddress,
              useHexPrefix,
            },
            {
              label: fm('toWalletAddress'),
              address: walletAddress,
              useHexPrefix,
            },
            {
              label: fm('transactionHistory'),
              address: orderStatusData.hash,
              useHexPrefix,
            },
          ]}
        >
          <>
            <div className={cls(styles.successDesc, styles.row)}>
              <div className={styles.successDescText}>
                {fm('redeemSuccessNum', {
                  num: '',
                })}
              </div>
              <MultiIconSpecial
                data={selectCoinList.map((contractAddress) => {
                  const coinInfo =
                    coinInfoStore[`${poolInfo.chainCode}${contractAddress}`];
                  const { amount } =
                    unstakeCoinAmountStore[contractAddress] || {};
                  return {
                    url: coinInfo.iconNightUrl,
                    amount: amount,
                    symbol: coinInfo.symbol as string,
                  };
                })}
              />
            </div>
            <div
              className={cls(styles.successDesc, styles.row)}
            >{`${fm('chainExecutionDelayNotice')}`}</div>
          </>
        </OrderResultSuccess>
      )}
      {FailArr.includes(orderStatusData.status) && (
        <OrderResultFail errorText={fm('operationNetworkIssueRetry')} />
      )}
      <div className={styles.btnContainer}>
        <Button className={styles.button} variant="primary" onClick={onConfirm}>
          {fm('ido_view_explorer')}
        </Button>
      </div>
    </Modal>
  );
};
